import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getApiBaseUrl } from '@core/services/api-base-url';
import { LookupResponse, normalizePageRequest, PageRequest, PagedResponse } from '@core/models/common.model';
import {
  CreateDependantRequest,
  DependantAuthorisationCodeResponse,
  CreateEmployeeRequest,
  DependantLookupResponse,
  DependantResponse,
  EmployeeImportEnqueueResponse,
  EmployeeImportResponse,
  EmployeeInterventionFilter,
  EmployeeInterventionResponse,
  EmployeeLookupResponse,
  EmployeeResponse,
  EmployeeTransactionHistoryResponse,
  EnqueueEmployeeImportRequest,
  UpdateDependantRequest,
  UpdateEmployeeRequest,
} from './employee.model';

const EMPLOYEE_IMPORTS_PATH = 'employees/imports';

@Injectable({ providedIn: 'root' })
export class EmployeeApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = getApiBaseUrl();

  getEmployees(pageRequest?: PageRequest, search?: string | null): Observable<PagedResponse<EmployeeResponse>> {
    const normalizedPageRequest = normalizePageRequest(pageRequest);

    return this.http.get<PagedResponse<EmployeeResponse>>(`${this.apiBaseUrl}employees`, {
      params: {
        search: search ?? '',
        pageNumber: normalizedPageRequest.pageNumber,
        pageSize: normalizedPageRequest.pageSize,
      },
    });
  }

  getEmployeeLookupResponses(search?: string | null, pageRequest?: PageRequest): Observable<PagedResponse<EmployeeLookupResponse>> {
    const normalizedPageRequest = normalizePageRequest(pageRequest);

    return this.http.get<PagedResponse<EmployeeLookupResponse>>(`${this.apiBaseUrl}employees/lookup-responses`, {
      params: {
        search: search ?? '',
        pageNumber: normalizedPageRequest.pageNumber,
        pageSize: normalizedPageRequest.pageSize,
      },
    });
  }

  getEmployee(employeeId: string): Observable<EmployeeResponse> {
    return this.http.get<EmployeeResponse>(`${this.apiBaseUrl}employees/${employeeId}`);
  }

  getEmployeeInterventions(
    employeeId: string,
    pageRequest?: PageRequest,
    filter: EmployeeInterventionFilter = {},
  ): Observable<PagedResponse<EmployeeInterventionResponse>> {
    const normalizedPageRequest = normalizePageRequest(pageRequest);
    const params: Record<string, string | number> = {
      pageNumber: normalizedPageRequest.pageNumber,
      pageSize: normalizedPageRequest.pageSize,
    };

    for (const [key, value] of Object.entries(filter)) {
      if (value) {
        params[key] = value;
      }
    }

    return this.http.get<PagedResponse<EmployeeInterventionResponse>>(
      `${this.apiBaseUrl}employees/${employeeId}/interventions`,
      { params },
    );
  }

  getEmployeeHistory(employeeId: string): Observable<EmployeeTransactionHistoryResponse[]> {
    return this.http.get<EmployeeTransactionHistoryResponse[]>(`${this.apiBaseUrl}employees/${employeeId}/history`);
  }

  createEmployee(request: CreateEmployeeRequest): Observable<EmployeeResponse> {
    return this.http.post<EmployeeResponse>(`${this.apiBaseUrl}employees`, request);
  }

  updateEmployee(employeeId: string, request: UpdateEmployeeRequest): Observable<EmployeeResponse> {
    return this.http.put<EmployeeResponse>(`${this.apiBaseUrl}employees/${employeeId}`, request);
  }

  deactivateEmployee(employeeId: string): Observable<EmployeeResponse> {
    return this.http.put<EmployeeResponse>(`${this.apiBaseUrl}employees/${employeeId}/deactivate`, {});
  }

  activateEmployee(employeeId: string): Observable<EmployeeResponse> {
    return this.http.put<EmployeeResponse>(`${this.apiBaseUrl}employees/${employeeId}/activate`, {});
  }

  getDependants(employeeId: string, includeInactive = false): Observable<DependantResponse[]> {
    return this.http.get<DependantResponse[]>(`${this.apiBaseUrl}employees/${employeeId}/dependants`, {
      params: {
        includeInactive,
      },
    });
  }

  sendDependantAuthorisationCode(employeeId: string, dependantId: string, mobileNumber: string | null): Observable<DependantAuthorisationCodeResponse> {
    return this.http.post<DependantAuthorisationCodeResponse>(
      `${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}/authorisation-code`,
      { mobileNumber },
    );
  }

  verifyDependantAuthorisationCode(employeeId: string, dependantId: string, code: string): Observable<DependantResponse> {
    return this.http.post<DependantResponse>(
      `${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}/authorisation-code/verify`,
      { code },
    );
  }

  getDependantLookupResponses(
    search?: string | null,
    pageRequest?: PageRequest,
    includeInactive = true,
  ): Observable<PagedResponse<DependantLookupResponse>> {
    const normalizedPageRequest = normalizePageRequest(pageRequest);

    return this.http.get<PagedResponse<DependantLookupResponse>>(`${this.apiBaseUrl}dependants/lookup-responses`, {
      params: {
        search: search ?? '',
        pageNumber: normalizedPageRequest.pageNumber,
        pageSize: normalizedPageRequest.pageSize,
        includeInactive,
      },
    });
  }

  getDependant(employeeId: string, dependantId: string, includeInactive = true): Observable<DependantResponse> {
    return this.http.get<DependantResponse>(`${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}`, {
      params: {
        includeInactive,
      },
    });
  }

  createDependant(employeeId: string, request: CreateDependantRequest): Observable<DependantResponse> {
    return this.http.post<DependantResponse>(`${this.apiBaseUrl}employees/${employeeId}/dependants`, request);
  }

  updateDependant(employeeId: string, dependantId: string, request: UpdateDependantRequest): Observable<DependantResponse> {
    return this.http.put<DependantResponse>(`${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}`, request);
  }

  deactivateDependant(employeeId: string, dependantId: string): Observable<DependantResponse> {
    return this.http.put<DependantResponse>(`${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}/deactivate`, {});
  }

  activateDependant(employeeId: string, dependantId: string): Observable<DependantResponse> {
    return this.http.put<DependantResponse>(`${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}/activate`, {});
  }

  getDependantConfirmationPreview(employeeId: string, dependantId: string, token: string): Observable<DependantResponse> {
    return this.http.get<DependantResponse>(
      `${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}/confirm`,
      { params: { token } },
    );
  }

  confirmDependant(employeeId: string, dependantId: string, token: string): Observable<DependantResponse> {
    return this.http.put<DependantResponse>(
      `${this.apiBaseUrl}employees/${employeeId}/dependants/${dependantId}/confirm`,
      {},
      { params: { token } },
    );
  }

  enqueueEmployeeImport(request: EnqueueEmployeeImportRequest): Observable<EmployeeImportEnqueueResponse> {
    const formData = new FormData();
    formData.append('File', request.file);

    const headers = request.idempotencyKey
      ? new HttpHeaders({
          'Idempotency-Key': request.idempotencyKey,
        })
      : undefined;

    return this.http.post<EmployeeImportEnqueueResponse>(`${this.apiBaseUrl}${EMPLOYEE_IMPORTS_PATH}`, formData, {
      headers,
    });
  }

  getEmployeeImports(pageRequest?: PageRequest): Observable<PagedResponse<EmployeeImportResponse>> {
    const normalizedPageRequest = normalizePageRequest(pageRequest);

    return this.http.get<PagedResponse<EmployeeImportResponse>>(`${this.apiBaseUrl}${EMPLOYEE_IMPORTS_PATH}`, {
      params: {
        pageNumber: normalizedPageRequest.pageNumber,
        pageSize: normalizedPageRequest.pageSize,
      },
    });
  }

  downloadEmployeeImportFile(importId: string): Observable<Blob> {
    return this.http.get(`${this.apiBaseUrl}${EMPLOYEE_IMPORTS_PATH}/${importId}/file`, {
      responseType: 'blob',
    });
  }

  downloadEmployeeImportErrors(importId: string): Observable<Blob> {
    return this.http.get(`${this.apiBaseUrl}${EMPLOYEE_IMPORTS_PATH}/${importId}/file/errors`, {
      responseType: 'blob',
    });
  }

  downloadEmployeeImportSummary(importId: string): Observable<Blob> {
    return this.http.get(`${this.apiBaseUrl}${EMPLOYEE_IMPORTS_PATH}/${importId}/file/summary`, {
      responseType: 'blob',
    });
  }
}
